import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { 
  TrendingUp, 
  ArrowRight, 
  Home, 
  Building, 
  RefreshCw,
  CheckCircle,
  Phone,
  Clock,
  Percent,
  DollarSign,
  Scale
} from 'lucide-react';

export default function LoansPage() {
  const { t } = useLanguage();

  const loanPrograms = [
    {
      id: 'fix-flip',
      icon: TrendingUp,
      title: t('loanPrograms.fixFlipTitle'),
      subtitle: t('howItWorks.step1Subtitle'),
      description: t('loanPrograms.fixFlipDesc'),
      terms: [
        { label: t('loans.loanAmount'), value: 'Up to $20M' },
        { label: t('loans.ltv'), value: 'Up to 75%' },
        { label: t('loans.term'), value: '12-24 months' },
        { label: t('loans.closing'), value: '5-7 days' },
      ],
      features: t('loanFeatures.fixFlip'),
    },
    {
      id: 'bridge',
      icon: ArrowRight,
      title: t('loanPrograms.bridgeTitle'),
      subtitle: t('loanPrograms.bridgeDesc'),
      description: t('loanPrograms.bridgeDesc'),
      terms: [
        { label: t('loans.loanAmount'), value: 'Up to $20M' },
        { label: t('loans.ltv'), value: 'Up to 75%' },
        { label: t('loans.term'), value: '6-24 months' },
        { label: t('loans.closing'), value: '5-7 days' },
      ],
      features: t('loanFeatures.bridge'),
    },
    {
      id: 'rental',
      icon: Home,
      title: t('loanPrograms.rentalTitle'),
      subtitle: t('loanPrograms.rentalDesc'),
      description: t('loanPrograms.rentalDesc'),
      terms: [
        { label: t('loans.loanAmount'), value: 'Up to $20M' },
        { label: t('loans.ltv'), value: 'Up to 60%' },
        { label: t('loans.term'), value: '5-30 years' },
        { label: t('loans.closing'), value: '2-3 weeks' },
      ],
      features: t('loanFeatures.rental'),
    },
    {
      id: 'commercial',
      icon: Building,
      title: t('loanPrograms.commercialTitle'),
      subtitle: t('loanPrograms.commercialDesc'),
      description: t('loanPrograms.commercialDesc'),
      terms: [
        { label: t('loans.loanAmount'), value: 'Up to $50M' },
        { label: t('loans.ltv'), value: 'Up to 70%' },
        { label: t('loans.term'), value: '1-5 years' },
        { label: t('loans.closing'), value: '1-3 weeks' },
      ],
      features: t('loanFeatures.commercial'),
    },
    {
      id: 'refinance',
      icon: RefreshCw,
      title: t('loanPrograms.refinanceTitle'),
      subtitle: t('loanPrograms.refinanceDesc'),
      description: t('loanPrograms.refinanceDesc'),
      terms: [
        { label: t('loans.loanAmount'), value: 'Up to $20M' },
        { label: t('loans.ltv'), value: 'Up to 75%' },
        { label: t('loans.term'), value: '1-30 years' },
        { label: t('loans.closing'), value: '5-7 days' },
      ],
      features: t('loanFeatures.refinance'),
    },
    {
      id: 'foreclosure',
      icon: Clock,
      title: t('loanPrograms.foreclosureTitle'),
      subtitle: t('loanPrograms.foreclosureDesc'),
      description: t('loanPrograms.foreclosureDesc'),
      terms: [
        { label: t('loans.loanAmount'), value: 'Up to $5M' },
        { label: t('loans.ltv'), value: 'Up to 75%' },
        { label: t('loans.term'), value: '6-24 months' },
        { label: t('loans.closing'), value: '5-7 days' },
      ],
      features: t('loanFeatures.foreclosure'),
    },
    {
      id: 'eviction',
      icon: Scale,
      title: t('loanPrograms.evictionTitle'),
      subtitle: t('loanPrograms.evictionDesc'),
      description: t('loanPrograms.evictionDesc'),
      terms: [
        { label: 'Service Fee', value: '$2,000' },
        { label: 'Type', value: 'Legal Services' },
        { label: 'Coverage', value: 'Eviction Defense' },
        { label: 'Response', value: 'Within 24 hours' },
      ],
      features: t('loanFeatures.eviction'),
    },
  ];

  return (
    <div className="pt-20" data-testid="loans-page">
      {/* Hero Section */}
      <section className="bg-slate-900 py-20 md:py-32">
        <div className="container-main">
          <div className="max-w-3xl">
            <p className="font-body text-amber-500 uppercase tracking-widest text-sm font-semibold mb-4">
              {t('loans.pageSubtitle')}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              {t('loans.pageTitle')}
            </h1>
            <p className="font-body text-slate-300 text-lg mb-8">
              {t('loans.pageDesc')}
            </p>
            <div className="flex flex-wrap gap-8">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-amber-500" />
                <div>
                  <p className="font-heading text-xl font-bold text-white">5-7 Days</p>
                  <p className="font-body text-slate-400 text-sm">{t('loans.fastClosing')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Percent className="w-6 h-6 text-amber-500" />
                <div>
                  <p className="font-heading text-xl font-bold text-white">Up to 75%</p>
                  <p className="font-body text-slate-400 text-sm">{t('loans.ltvAvailable')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <DollarSign className="w-6 h-6 text-amber-500" />
                <div>
                  <p className="font-heading text-xl font-bold text-white">$75K - $10M</p>
                  <p className="font-body text-slate-400 text-sm">{t('loans.loanAmounts')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Loan Programs */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="space-y-16">
            {loanPrograms.map((program, index) => (
              <div 
                key={program.id}
                id={program.id}
                className="scroll-mt-24"
                data-testid={`loan-detail-${program.id}`}
              >
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-start ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  {/* Content */}
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 bg-slate-900 flex items-center justify-center">
                        <program.icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
                          {program.title}
                        </h2>
                      </div>
                    </div>
                    <p className="font-body text-slate-600 text-lg mb-8">
                      {program.description}
                    </p>
                    
                    {/* Features */}
                    <div className="mb-8">
                      <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">
                        {t('loans.programFeatures')}
                      </h3>
                      <ul className="space-y-3">
                        {program.features && program.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                            <span className="font-body text-slate-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link to="/contact">
                      <Button className="btn-gold rounded-none" data-testid={`apply-${program.id}`}>
                        {t('loans.applyFor')} {program.title}
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </Link>
                  </div>

                  {/* Terms Card */}
                  <div className={`bg-slate-50 border border-slate-200 p-8 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <h3 className="font-heading text-xl font-bold text-slate-900 mb-6">
                      {t('loans.loanTerms')}
                    </h3>
                    <div className="grid grid-cols-2 gap-6">
                      {program.terms.map((term, i) => (
                        <div key={i}>
                          <p className="font-body text-slate-500 text-sm mb-1">{term.label}</p>
                          <p className="font-heading text-xl font-bold text-slate-900">{term.value}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-8 pt-6 border-t border-slate-200">
                      <p className="font-body text-xs text-slate-500">
                        {t('loans.termsDisclaimer')}
                      </p>
                    </div>
                  </div>
                </div>

                {index < loanPrograms.length - 1 && (
                  <div className="border-b border-slate-200 mt-16" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-20">
        <div className="container-main text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            {t('loans.notSure')}
          </h2>
          <p className="font-body text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            {t('loans.notSureDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="btn-gold rounded-none" data-testid="loans-cta-btn">
                {t('loans.submitYourDeal')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:424-303-2130">
              <Button className="btn-secondary rounded-none bg-transparent border-white text-white hover:bg-white/10">
                <Phone className="mr-2 w-5 h-5" />
                {t('cta.call')} 424-303-2130
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
